import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { ChevronLeft, ChevronRight, Flame, Pencil, Plus, Trash2 } from 'lucide-react';
import { journalStats } from '@habitos/assistant/domain/journalStats';
import { toEngineSettings } from '../domain/model';
import { addDays, type LocalDate } from '@habitos/engine';
import { useI18n } from '../i18n';
import { useApp } from '../state';
import type { JournalRec } from '../domain/model';
import { deleteJournal, entriesOn, isEmptyDraft, JOURNAL_PROMPTS, restoreJournal, saveJournal, suggestedPrompt, type JournalDraft } from '../domain/journal';
import { Empty } from '../ui/Empty';
import { Field, Switch } from '../ui/Field';
import { useToast } from '../ui/Toast';

/** Diario breve dentro de Cerebro. */
export function JournalPanel() {
  const { t, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const toast = useToast();
  const [day, setDay] = useState<LocalDate>(ctx.today);
  const [editing, setEditing] = useState<JournalRec | 'new' | null>(null);
  const entries = entriesOn(data.journal, day);

  return (
    <div className="stack">
      <JournalSummary />
      <nav className="row" aria-label={t('journal.dayNav')} style={{ justifyContent: 'space-between' }}>
        <button type="button" className="icon-btn" aria-label={t('agenda.prevDay')} onClick={() => setDay(addDays(day, -1))}>
          <ChevronLeft size={22} aria-hidden="true" />
        </button>
        <strong aria-live="polite">{day === ctx.today ? t('agenda.today') : fmtDate(day, { weekday: 'long', day: 'numeric', month: 'long' })}</strong>
        <button type="button" className="icon-btn" aria-label={t('agenda.nextDay')} disabled={day >= ctx.today} onClick={() => setDay(addDays(day, 1))}>
          <ChevronRight size={22} aria-hidden="true" />
        </button>
      </nav>

      {editing ? (
        <JournalEditor key={editing === 'new' ? `new-${day}` : editing.id} day={day} entry={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      ) : (
        <button type="button" className="btn btn--primary" onClick={() => setEditing('new')}>
          <Plus size={18} aria-hidden="true" /> {t('journal.write')}
        </button>
      )}

      {entries.length === 0 && !editing ? (
        <Empty title={t('journal.emptyTitle')} body={t('journal.emptyBody')} />
      ) : (
        entries
          .filter((e) => editing === null || editing === 'new' || editing.id !== e.id)
          .map((e) => (
            <article key={e.id} className="card stack" aria-label={t('journal.entryAt', { time: new Date(e.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) })}>
              {e.text && <p style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', margin: 0 }}>{e.text}</p>}
              {e.prompts.map((p) => (
                <div key={p.key} className="small">
                  <strong>{t(`journal.prompts.${p.key}` as 'journal.prompts.went_well')}</strong>
                  <div style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{p.answer}</div>
                </div>
              ))}
              <div className="row" style={{ flexWrap: 'wrap' }}>
                {e.includeInReview && <span className="badge badge--accent">{t('journal.inReview')}</span>}
                <span style={{ flex: 1 }} />
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditing(e)}>
                  <Pencil size={16} aria-hidden="true" /> {t('common.edit')}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={async () => {
                    await deleteJournal(e.id);
                    toast.show(t('journal.deleted'), { label: t('common.undo'), run: () => void restoreJournal(e.id) });
                  }}
                >
                  <Trash2 size={16} aria-hidden="true" /> {t('common.delete')}
                </button>
              </div>
            </article>
          ))
      )}
      <Link to="/review" className="small">
        {t('review.open')}
      </Link>
    </div>
  );
}

/** Editor con guardado automático: guarda 800 ms después del último cambio. */
function JournalEditor({ day, entry, onClose }: { day: LocalDate; entry: JournalRec | null; onClose: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const [draft, setDraft] = useState<JournalDraft>(() => ({
    date: entry?.date ?? day,
    text: entry?.text ?? '',
    prompts: entry?.prompts.length ? entry.prompts : [{ key: suggestedPrompt(day), answer: '' }],
    includeInReview: entry?.includeInReview ?? false,
  }));
  const [status, setStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const idRef = useRef<string | null>(entry?.id ?? null);
  const firstRender = useRef(true);

  useEffect(() => {
    if (firstRender.current) {
      firstRender.current = false;
      return;
    }
    if (!idRef.current && isEmptyDraft(draft)) return;
    const timer = window.setTimeout(() => {
      setStatus('saving');
      saveJournal(idRef.current, draft)
        .then((id) => {
          idRef.current = id;
          setStatus('saved');
        })
        .catch(() => setStatus('error'));
    }, 800);
    return () => window.clearTimeout(timer);
  }, [draft]);

  const unused = JOURNAL_PROMPTS.filter((k) => !draft.prompts.some((p) => p.key === k));

  return (
    <form
      className="card stack"
      aria-label={t('journal.editor')}
      onSubmit={async (e) => {
        e.preventDefault();
        if (!isEmptyDraft(draft) || idRef.current) {
          try {
            idRef.current = await saveJournal(idRef.current, draft);
          } catch {
            toast.show(t('errors.save'));
            return;
          }
        }
        onClose();
      }}
    >
      <Field label={t('journal.text')}>
        {(p) => <textarea {...p} className="input" rows={5} maxLength={20_000} value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />}
      </Field>
      {draft.prompts.map((pr, i) => (
        <Field key={pr.key} label={t(`journal.prompts.${pr.key}` as 'journal.prompts.went_well')} help={i === 0 ? t('journal.optional') : undefined}>
          {(p) => (
            <textarea
              {...p}
              className="input"
              rows={2}
              maxLength={4000}
              value={pr.answer}
              onChange={(e) => setDraft({ ...draft, prompts: draft.prompts.map((x) => (x.key === pr.key ? { ...x, answer: e.target.value } : x)) })}
            />
          )}
        </Field>
      ))}
      {unused.length > 0 && (
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setDraft({ ...draft, prompts: [...draft.prompts, { key: unused[0]!, answer: '' }] })}>
          {t('journal.anotherPrompt')}
        </button>
      )}
      <Switch label={t('journal.includeInReview')} help={t('journal.includeHelp')} checked={draft.includeInReview} onChange={(v) => setDraft({ ...draft, includeInReview: v })} />
      <p className="muted xsmall" aria-live="polite" data-testid="journal-status">
        {status === 'saving' ? t('journal.saving') : status === 'saved' ? t('journal.saved') : status === 'error' ? t('errors.save') : ''}
      </p>
      <button type="submit" className="btn btn--primary btn--block">
        {t('journal.done')}
      </button>
    </form>
  );
}

/**
 * Resumen del diario con datos reales: saludo y pregunta del día, racha actual, mejor racha,
 * entradas, días escritos de esta semana y cuánto falta para la revisión semanal.
 */
function JournalSummary() {
  const { t, fmtDate } = useI18n();
  const { data, ctx } = useApp();
  const s = useMemo(() => journalStats(data.journal, ctx.today, toEngineSettings(ctx.settings)), [data.journal, ctx.today, ctx.settings]);
  const hour = Number(new Intl.DateTimeFormat('en-GB', { timeZone: ctx.tz, hour: '2-digit', hourCycle: 'h23' }).format(new Date()));
  const greeting = t(`today.greeting.${hour >= 5 && hour < 13 ? 'morning' : hour >= 13 && hour < 20 ? 'afternoon' : 'evening'}`);
  const prompt = suggestedPrompt(ctx.today);
  return (
    <section className="card stack journal-sum" aria-labelledby="journal-sum-title">
      <div>
        <p className="page-header__greeting">{greeting}</p>
        <h2 id="journal-sum-title" style={{ margin: 0 }}>
          {t(`journal.prompts.${prompt}` as 'journal.prompts.went_well')}
        </h2>
        <p className="muted small" style={{ margin: 0 }}>
          {fmtDate(ctx.today, { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>
      <dl className="journal-sum__stats">
        <div>
          <dt>{t('journal.stats.current')}</dt>
          <dd className="num">
            <Flame size={16} aria-hidden="true" /> {s.current}
          </dd>
        </div>
        <div>
          <dt>{t('journal.stats.best')}</dt>
          <dd className="num">{s.best}</dd>
        </div>
        <div>
          <dt>{t('journal.stats.entries')}</dt>
          <dd className="num">{s.entries}</dd>
        </div>
      </dl>
      <div className="journal-sum__week" role="list" aria-label={t('journal.stats.week')}>
        {s.week.map((w) => (
          <span
            key={w.day}
            role="listitem"
            className={`journal-sum__day${w.written ? ' journal-sum__day--on' : ''}${w.day === ctx.today ? ' journal-sum__day--today' : ''}`}
            aria-label={`${fmtDate(w.day, { weekday: 'long' })}: ${w.written ? t('journal.stats.written') : w.future ? t('journal.stats.upcoming') : t('journal.stats.notWritten')}`}
          >
            {t(`journal.stats.letters.${w.weekday}`)}
          </span>
        ))}
      </div>
      <p className="small" style={{ margin: 0 }}>
        {s.daysToReview === 1 ? t('journal.stats.reviewToday') : t('journal.stats.reviewIn', { count: s.daysToReview })}
      </p>
    </section>
  );
}
